import {usePrevious} from '@aboutyou/utils';
import React, {startTransition, useEffect, useRef, useState} from 'react';
import {MAX_TIMEOUT_WAIT_IN_MS} from '../../../features/constants/constants';
import {
  DiscountNotificationType,
  useDiscountNotification,
} from '../hooks/useDiscountNotification';
import {useDiscountsRequest} from '../hooks/useDiscounts';
import {CampaignReload} from './CampaignReload';

export const DiscountsRefresh = () => {
  const {refetch, data, error} = useDiscountsRequest();
  const getDiscountsFetchFailed = Boolean(error);
  const {showNotification} = useDiscountNotification();
  const nextUpdate = data?.nextUpdate
    ? new Date(data?.nextUpdate).getTime()
    : undefined;
  const timeout = useRef<NodeJS.Timeout>(undefined);
  const now = useRef(new Date().getTime());

  const previousNextUpdate = usePrevious(nextUpdate);

  const [doPageReload, setDoPageReload] = useState(false);

  /**
   * when a campaign end abruptly when someone deletes it or reduce the time in backoffice,
   * the nextUpdate field disapears. In this case we force a refresh of the page
   */
  useEffect(() => {
    if (getDiscountsFetchFailed) return; // do not show popup when fetching discounts failed

    if (previousNextUpdate && !nextUpdate) {
      requestAnimationFrame(() => {
        startTransition(() => {
          // eslint-disable-next-line local-rules/set-state-in-use-effect
          setDoPageReload(true);
        });
      });
    }
  }, [previousNextUpdate, nextUpdate, getDiscountsFetchFailed]);

  const [campaignId, setCampaignId] = useState<string | undefined>();

  // eslint-disable-next-line local-rules/too-complex-useeffects -- TODO: Cleanup complex useEffect https://aboutyou.atlassian.net/browse/ASFE-7605
  useEffect(() => {
    if (nextUpdate) {
      const diff = nextUpdate - now.current;
      timeout.current = setTimeout(async () => {
        const previousPriceReductionCampaign = data?.discount;

        const {data: refetchData} = await refetch();

        if (refetchData) {
          const activeDiscount = refetchData.discount;
          const currentPriceReductionCampaign = activeDiscount;

          /**
           * If we go from a price reduction campaign to no active discount situation or vice versa, we need to reload the page
           * This is because the price reduction campaign key is not part of any GRPC requests, a full reload is required in this case
           */
          if (
            (previousPriceReductionCampaign || currentPriceReductionCampaign) &&
            activeDiscount?.id !== previousPriceReductionCampaign?.id
          ) {
            requestAnimationFrame(() => {
              startTransition(() => {
                // eslint-disable-next-line local-rules/set-state-in-use-effect
                setDoPageReload(true);
                setCampaignId(activeDiscount?.id);
              });
            });
            return;
          }

          // A previously available discount ended and no new one available
          if (data?.discount?.id && !activeDiscount) {
            showNotification({
              type: DiscountNotificationType.ExpiredWithEmptyDiscounts,
            });
          }
        }
        /**
         * This is required because `setTimeout`'s internals only allow up to 2147483647 milliseconds since
         * timeout values too big to fit into a signed 32-bit integer may cause overflow in FF, Safari, and Chrome,
         * resulting in the timeout being scheduled immediately.
         *
         * https://nodejs.org/api/timers.html#settimeoutcallback-delay-args
         */
      }, Math.min(diff, MAX_TIMEOUT_WAIT_IN_MS));
    }

    return () => timeout.current && clearTimeout(timeout.current);
  }, [nextUpdate]);

  return doPageReload ? <CampaignReload campaignId={campaignId} /> : null;
};
